import { Component } from "react";
import styles from "./cart-item.module.less";
import {
  extract,
  ECommerceHelper,
  DateHelper,
  EventAggregator,
  createDate,
  ProductStore,
  RideStore,
  QuantitySelector,
  Icon,
} from "@parksandresorts/core";
import LinkWrapper from "../link-wrapper/link-wrapper";
import SiteConfig from "components/stores/site-configuration-store";

export default class CartItem extends Component {
  constructor(props) {
    super(props);

    this.currencyDisplayName = extract(ECommerceHelper, "jetshop.currencyDisplayName");
    this.onlyValidText = extract(ECommerceHelper, "jetshop.onlyValidText");

    this.bookableProductInformation = this.getBookableInformation();

    this.expressPassLabel = SiteConfig.getLabel("CART_EXPRESSPASS_HEADING", "Tidsbokning");

    this.state = {
      productName: this.props.name,
      productImageUrl: this.props.imageUrl,
      productUrl: "",
      productThumbnailUrl: this.props.imageUrl,
      expressPassLoaded: false,
    };
  }

  componentDidMount() {
    let product = ProductStore.getProductByJetshopId(this.props.jetshopId);
    if (!product) {
      // If non-Swedish product cant be found, get Swedish one
      product = ProductStore.getProductByJetshopId(this.props.jetshopId, "sv");
    }

    const productImageUrl = extract(product, "productImage.file.url");
    let expressPassLoaded = this.state.expressPassLoaded;
    if (this.props.expressPass) {
      this.expressPass = {
        customerName: extract(this.props, "expressPass.customerName"),
        timeSlots: this.getTimeSlotsForJetPass(),
      };
      expressPassLoaded = true;
    }

    this.setState({
      productName: extract(product, "productName", this.state.productName),
      productImageUrl: productImageUrl,
      productUrl: extract(product, "productPageLink.slug", this.state.productUrl),
      productThumbnailUrl: extract(product, "cartThumbnail.file.url", productImageUrl),
      expressPassLoaded: expressPassLoaded,
    });
  }

  getTimeSlotsForJetPass() {
    const extendedTimeSlots = [];
    const timeSlots = extract(this.props, "expressPass.timeSlots", []);
    for (const timeSlot of timeSlots) {
      const ride = RideStore.getRideById(timeSlot.id);
      extendedTimeSlots.push({
        ...timeSlot,
        timeSlotBegin: timeSlot.timeSlotBegin ? createDate(timeSlot.timeSlotBegin) : null,
        rideName: ride ? ride.title : "",
      });
    }

    return extendedTimeSlots;
  }

  getBookableInformation() {
    if (!this.props.bookableItem) {
      return undefined;
    }

    const isFullDay = extract(this.props, "bookableItem.isFullDay");
    const timeSlotBegin = extract(this.props, "bookableItem.timeSlotBegin");
    if (!timeSlotBegin) {
      return undefined;
    }

    const begin = createDate(timeSlotBegin);

    return isFullDay
      ? `${this.onlyValidText ? this.onlyValidText + " " : ""}${DateHelper.toStringDDMonth(begin)}`
      : `${DateHelper.toStringDDMonth(begin)} ${DateHelper.toStringTimeHHMM(begin)}`;
  }

  formatProductObj(quantity = this.props.quantity) {
    return {
      productName: this.props.name,
      jetshopCartItemId: this.props.jetshopCartItemId,
      jetshopId: this.props.jetshopId,
      price: this.props.price,
      quantity: quantity,
    };
  }

  onChangeQuantity = (quantity) => {
    const incOrDecQuantity = quantity - this.props.quantity;

    if (incOrDecQuantity === -1 && (this.props.quantity === 1 || this.props.quantity === this.minimumQuantity)) {
      this.props.cartContext.deleteCartItem(this.formatProductObj());
    } else {
      this.props.cartContext.updateCartItem(
        this.formatProductObj(this.props.quantity + incOrDecQuantity),
        incOrDecQuantity,
      );
    }

    if (incOrDecQuantity === 1) {
      EventAggregator.publish("trackEvent", {
        category: "Cart",
        action: "Added to Cart",
        label: this.productName,
      });
    } else {
      EventAggregator.publish("trackEvent", {
        category: "Cart",
        action: "Removed from Cart",
        label: this.productName,
      });
    }
  };

  render() {
    return (
      <div className={styles.cartItem}>
        {this.state.productImageUrl && (
          <LinkWrapper
            url={this.state.productUrl}
            className={styles.cartItem__imageWrapper}
            onClick={() => {
              EventAggregator.publish("trackEvent", {
                category: "Cart",
                action: "Clicked link",
                label: this.state.productName,
              });
            }}
          >
            <div
              className={styles.cartItem__image}
              style={{
                backgroundImage: `url(${
                  this.state.productThumbnailUrl ? this.state.productThumbnailUrl : this.state.productImageUrl
                })`,
              }}
            />
          </LinkWrapper>
        )}
        <div className={styles.cartItem__actions}>
          {this.state.productName && this.state.productUrl ? (
            <LinkWrapper
              url={this.state.productUrl}
              className={styles.name}
              onClick={() => {
                EventAggregator.publish("trackEvent", {
                  category: "Cart",
                  action: "Clicked link",
                  label: this.state.productName,
                });
              }}
            >
              {this.state.productName}
              <Icon name="chevron-right" className={styles.icon} size="xxs" />
              {this.bookableProductInformation && (
                <span className={`${styles.bookableDate} body-s`}>{`(${this.bookableProductInformation})`}</span>
              )}
              {this.state.expressPassLoaded && (
                <div className={styles.expressPassWrapper}>
                  <hr className={styles.expressPassDivider} />
                  <span className={`${styles.expressPassHeading} body-s`}>{this.expressPassLabel}</span>
                  <ul className={styles.expressPassList}>
                    {this.expressPass.timeSlots.map((timeSlot) => (
                      <li key={timeSlot.id} className="body-xs">
                        {DateHelper.toStringTimeHHMM(timeSlot.timeSlotBegin)} - {timeSlot.rideName}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </LinkWrapper>
          ) : (
            <span className={styles.name}>
              {`${this.state.productName ? this.state.productName : ""}`}
              {this.bookableProductInformation && (
                <span className={`${styles.bookableDate} p_small`}>{`(${this.bookableProductInformation})`}</span>
              )}
            </span>
          )}
          {this.props.price >= 0 && (
            <>
              <div className={styles.cartItem__quantity}>
                <QuantitySelector
                  quantity={this.props.quantity}
                  maxQuantity={this.props.stockQuantity}
                  onQuantityChanged={(q) => this.onChangeQuantity(q)}
                  wrapperClassName={styles.quantityWrapper}
                  classNameLeftButton={styles.quantityLeftBtn}
                  classNameRightButton={styles.quantityRightBtn}
                  quantityClassName={styles.quantityQty}
                />
              </div>
              <span className={styles.cartItem__price}>{`${Math.round(this.props.price) * this.props.quantity} ${
                this.currencyDisplayName
              }`}</span>
            </>
          )}
        </div>
      </div>
    );
  }
}
